import { useContext } from 'react'
import Clickstream from '@gojek/clickstream-web/types/clickstream'
import { products } from '@gojek/web-gopay-protos'
import { detect } from 'detect-browser'
import EventsContext from 'contexts/EventsContext'
import { getTimestampInSeconds } from 'utils/utility'
const { Page, Component } = products.events.ui

export const CLICKSTREAM_EVENT_UI_PAGE = 'page'
export const CLICKSTREAM_EVENT_UI_COMPONENT = 'component'

function useEvents() {
    const eventsContext = useContext(EventsContext)
    return eventsContext
}

abstract class TrackerFactory {
    data: any = {}
    browser: any = null

    constructor() {
        this.browser = detect()
        this._addData({
            meta: {
                device: {
                    browser: {
                        name: this.browser?.name,
                        userAgent: navigator.userAgent,
                        version: this.browser?.version,
                    },
                },
            },
        })
    }

    public abstract getInstance(): IEvent

    _addData(data: any): void {
        // add common metadata
        this.data = { ...this.data, ...data }
    }
}

class ComponentTracker extends TrackerFactory {
    event: Clickstream
    constructor(clickstreamEvent: Clickstream) {
        super()
        this.event = clickstreamEvent
    }

    public getInstance(): IEvent {
        return new ComponentEvent(this.event, this.data)
    }
}

class PageTracker extends TrackerFactory {
    event: Clickstream
    constructor(clickstreamEvent: Clickstream) {
        super()
        this.event = clickstreamEvent
    }

    public getInstance(): IEvent {
        return new PageEvent(this.event, this.data)
    }
}

class ActionTracker extends TrackerFactory {
    public getInstance(): IEvent {
        return new ActionEvent(this.data)
    }
}

interface IEvent {
    track: (eventType: string, action?: number, data?: any) => Promise<any>
}

class ComponentEvent implements IEvent {
    event: Clickstream
    data: any
    constructor(event: Clickstream, data: any) {
        this.event = event
        this.data = data
    }

    _addData(data: any) {
        this.data = { ...this.data, ...data }
    }

    async track(eventType: string, action = 0, data = {}) {
        this._addData({
            eventName: eventType,
            action,
            ...data,
            eventTimestamp: {
                seconds: getTimestampInSeconds(),
            },
        })
        return await this.event.track(Component.create(this.data))
    }
}

class PageEvent implements IEvent {
    event: Clickstream
    data: any
    constructor(event: Clickstream, data: any) {
        this.event = event
        this.data = data
    }

    _addData(data: any) {
        this.data = { ...this.data, ...data }
    }

    async track(eventType: string, data = {}) {
        this._addData({
            eventName: eventType,
            ...data,
            eventTimestamp: {
                seconds: getTimestampInSeconds(),
            },
        })
        return await this.event.track(Page.create(this.data))
    }
}

class ActionEvent implements IEvent {
    data: any
    constructor(data: any) {
        this.data = data
    }

    _addData(data: any) {
        this.data = { ...this.data, ...data }
    }

    async track(eventType: string, data = {}): Promise<any> {
        this._addData({
            eventName: eventType,
            ...data,
            eventTimestamp: {
                seconds: getTimestampInSeconds(),
            },
        })
        return window.newrelic?.addPageAction(eventType, this.data)
    }
}

function useEventTracker() {
    const clickstreamEvent = useEvents()
    const trackPage = new PageTracker(clickstreamEvent).getInstance().track
    const trackComponent = new ComponentTracker(clickstreamEvent).getInstance()
        .track
    const trackAction = new ActionTracker().getInstance().track

    const logEvent = (
        eventType: string,
        target: string = '',
        action: number = 0,
        data: any = {}
    ) => {
        trackAction(eventType, action, data) // log the new relic action for all events.

        if (target === CLICKSTREAM_EVENT_UI_PAGE)
            trackPage(eventType, action, data)
        if (target === CLICKSTREAM_EVENT_UI_COMPONENT)
            trackComponent(eventType, action, data)
    }

    return {
        trackPage,
        trackComponent,
        trackAction,
        logEvent,
    }
}

export default useEventTracker
